// Iter 221 — Shared mode-aware weighted-wager helper with 90-day projection.
//
// Mirrors the backend `_compute_casino_weighted_wager` +
// `_project_weighted` chain EXACTLY:
//   raw = total_only ? total×lump[vertical] : Σ(cat × coef)
//   weighted = raw × (90 / periodDays)     ← Iter 221 projection
//
// Raw entered figures stay stored verbatim per Iter 194. This helper
// consolidates the DERIVE step (raw → projected weighted) into one
// place so display and validation can never drift. The Offered Volume
// 50–200% band on FE + BE both feed off the projected number.
//
// Casino reads `p.wagerReportPeriodDays`; Case reads `p.caseReportPeriodDays`.
// If a legacy doc has no period field → default 90 (identity, matches
// backend `_project_weighted`).
//
// coeffCfg shape: { categories: [{key, coefficient}], lump: {casino, case_opening} }

const DEFAULT_LUMP = { casino: 0.45, case_opening: 1.0 };

export function projectWeighted(rawWeighted, periodDays) {
  const p = Number(periodDays) || 90;
  const eff = p > 0 ? p : 90;
  return Math.round((Number(rawWeighted) || 0) * (90 / eff) * 100) / 100;
}

export function weightedFor(vertical, p, coeffCfg) {
  const mode = (p && p.wagerReportMode) || "categorized";
  const lumpCfg = (coeffCfg && coeffCfg.lump) || {};
  let raw = 0;
  let periodDays = 90;
  if (vertical === "casino") {
    periodDays = Number((p && p.wagerReportPeriodDays)) || 90;
    if (mode === "total_only") {
      const lump = Number(lumpCfg.casino ?? DEFAULT_LUMP.casino);
      const total = Number((p && p.wagerTotalSelfReported) || 0);
      raw = total * lump;
    } else {
      const cats = (coeffCfg && coeffCfg.categories) || [];
      const amounts = (p && p.wagerCategoriesSelfReported) || {};
      raw = cats.reduce(
        (a, c) => a + Number(amounts[c.key] || 0) * Number(c.coefficient || 0),
        0,
      );
    }
  } else if (vertical === "case_opening") {
    periodDays = Number((p && p.caseReportPeriodDays)) || 90;
    // Backend stores caseWagerSelfReported as the effective total; case
    // lump coefficient is 1.0.
    const lump = Number(lumpCfg.case_opening ?? DEFAULT_LUMP.case_opening);
    const total = Number((p && p.caseWagerSelfReported) || 0);
    raw = total * lump;
  } else {
    return 0;
  }
  return projectWeighted(raw, periodDays);
}

// Whether a player has any self-reported casino data — mode aware.
export function hasCasinoSelfReported(p) {
  const mode = (p && p.wagerReportMode) || "categorized";
  if (mode === "total_only") {
    return Number((p && p.wagerTotalSelfReported) || 0) > 0;
  }
  const amounts = (p && p.wagerCategoriesSelfReported) || {};
  return Object.values(amounts).some((v) => Number(v) > 0);
}
